// TO-DO: returning colors from theme is not working, so we are using strings for now.

import { TagColorScheme } from "@shared/components/ui-kit/data-display/Tag/Tag.props";
import {
  DataProvider,
  DEFAULT_MAX_TICKET_PURCHASE_LIMIT,
  DEFAULT_MIN_TICKET_PURCHASE_LIMIT,
} from "@shared/constants/common";
import { TicketTypeSerializer } from "@shared/modules/events/types/public";
import { PublicMembershipLevelSerializer } from "@shared/modules/memberships/types";
import {
  SeatingEventPropertyMap,
  SeatingItemPropertyMap,
} from "@shared/modules/seating/types/seating-property-types";
import {
  FormattedSeatWithLocationDataType,
  SeatWithLocationDataType,
} from "@shared/modules/seating/types/seating-types";

export const getRankStatus = (rank: string): TagColorScheme => {
  const rankNumber = parseInt(rank, 10);

  if (rankNumber >= 1 && rankNumber <= 3) {
    return "error";
  }
  if (rankNumber >= 4 && rankNumber <= 7) {
    return "warning";
  }
  if (rankNumber >= 8 && rankNumber <= 10) {
    return "success";
  }

  return "warning"; // default fallback
};

export const calculateSubtotal = (items: SeatWithLocationDataType[]) => {
  return items.reduce((acc, item) => {
    if (!item.price) return acc;

    const quantity = item.qty ?? 1;

    // Handle price if it's a string (e.g. "$131.78 CAD")
    if (typeof item.price === "string") {
      // Extract numeric value from price string
      const numericValue = parseFloat(item.price.replace(/[^0-9.]/g, ""));
      return acc + (Number.isNaN(numericValue) ? 0 : numericValue * quantity);
    }

    // Handle price if it's already a number
    return acc + (item.price ?? 0) * quantity;
  }, 0);
};

export const totalItemsInCart = (items: SeatWithLocationDataType[]) => {
  return items.reduce((acc, item) => {
    return acc + (item.qty ?? 1);
  }, 0);
};

export const getRankLabel = (rank: string): string => {
  const rankNumber = parseInt(rank, 10);

  if (rankNumber >= 1 && rankNumber <= 3) {
    return "Bad";
  }
  if (rankNumber >= 4 && rankNumber <= 7) {
    return "Average";
  }
  if (rankNumber >= 8 && rankNumber <= 10) {
    return "Amazing";
  }

  return "Average"; // default fallback
};

export const getUniqueId = (seatingInfo: {
  identifier: string;
  segment_identifier: string;
  location_identifier: string;
  [key: string]: unknown;
}) => {
  return `${seatingInfo.identifier}_${seatingInfo.segment_identifier}_${seatingInfo.location_identifier}`;
};

export const getPurchaseLimitsFromSeatingItemArray = (
  seatingItemArray?: SeatingItemPropertyMap[],
  basketType: DataProvider = DataProvider.Public
) => {
  if (!seatingItemArray) {
    return {
      min: DEFAULT_MIN_TICKET_PURCHASE_LIMIT,
      max: DEFAULT_MAX_TICKET_PURCHASE_LIMIT,
    };
  }

  return seatingItemArray.reduce(
    (acc, item) => {
      return {
        min: Math.min(
          acc.min,
          item.minimum_purchase_limit || DEFAULT_MIN_TICKET_PURCHASE_LIMIT
        ),
        max: Math.max(
          acc.max,
          basketType === DataProvider.Public
            ? item.purchase_limit
            : item.box_office_purchase_limit || item.purchase_limit
        ),
      };
    },
    { min: Infinity, max: 0 }
  );
};

export const findTicketTypeInfo = (
  seatingEventData: SeatingEventPropertyMap | undefined,
  seat: FormattedSeatWithLocationDataType,
  seatingItemArray?: SeatingItemPropertyMap[]
) => {
  if (seatingEventData?.ticket_types) {
    return seatingEventData.ticket_types.find(
      (item: TicketTypeSerializer) =>
        item.seat_permissions?.includes(seat.permissionId) ||
        item.id === seat.ticketTypeId
    );
  }

  if (seatingEventData?.tickettype_set) {
    const ticketType = seatingItemArray?.find(
      (item) => item.id === seat.ticketTypeId || item.id === seat.itemTypeId
    );
    return ticketType;
  }

  if (seatingEventData?.membership_levels) {
    return seatingEventData.membership_levels.find(
      (item: PublicMembershipLevelSerializer) =>
        item.id === seat.membershipLevelId || item.id === seat.itemTypeId
    );
  }

  return undefined;
};

/**
 * Formats seating objects with ticket type information (price and label)
 * @param seatingObjectArray Array of seat objects
 * @param seatingEventData Event data containing ticket types
 * @returns Formatted seat objects with price and label
 */
export const formatSeatingObjectsWithTicketInfo = (
  seatingObjectArray: SeatWithLocationDataType[] = [],
  seatingEventData: SeatingEventPropertyMap | undefined = undefined,
  seatingItemArray?: SeatingItemPropertyMap[]
) => {
  return seatingObjectArray.map((seat) => {
    const selectedTicketInfo = findTicketTypeInfo(
      seatingEventData,
      seat as FormattedSeatWithLocationDataType,
      seatingItemArray
    );

    return {
      ...seat,
      price: seat.price || selectedTicketInfo?.price,
      label: selectedTicketInfo?.name,
    };
  });
};
